import React, { useEffect, useRef, useCallback } from 'react';
import { MapContainer, TileLayer, useMap } from 'react-leaflet';
import L, { LatLngTuple } from 'leaflet';
import 'leaflet/dist/leaflet.css';
import styles from './styles_map.module.css';
import { EntTerritorialsUnits } from '../../../../models/handBooks/territorialsUnits';
import { ResponseDataGet } from '../../../../models/responseDataGet';
import { TransportTypesResponse, TransportTypeCount } from '../api/types';
import LeasingSelectYear from '../Leasingselect/index';

interface LeasingMapProps {
  latitude: number;
  longitude: number;
  polygonsData?: ResponseDataGet<EntTerritorialsUnits>;
  transportTypes?: TransportTypesResponse;
  isLoading: boolean;
  onRegionClick: (regionId: number, regionName: string) => void;
  selectedRegionName: string;
  selectedYear: number | null;
  onYearChange: (year: number | null) => void;
  initialRegionId?: number;
}

const parsePolygonData = (polygon: EntTerritorialsUnits): LatLngTuple[] | null => {
  try {
    const parsedPolygon = typeof polygon.polygonJson === 'string'
      ? JSON.parse(polygon.polygonJson)
      : polygon.polygonJson;

    const geoJson = parsedPolygon[0]?.geojson;
    if (!geoJson || !Array.isArray(geoJson.coordinates) || geoJson.coordinates.length === 0) {
      return null;
    }
    const coordinates = geoJson.type === 'MultiPolygon'
      ? geoJson.coordinates[0][0]
      : geoJson.coordinates[0];

    if (!Array.isArray(coordinates)) return null;

    return coordinates.map((coord: number[]): LatLngTuple => [coord[1], coord[0]]);
  } catch (error) {
    console.error(`Ошибка обработки полигона ${polygon.nameRu}:`, error);
    return null;
  }
};

const MapComponent: React.FC<{
  polygonsData?: ResponseDataGet<EntTerritorialsUnits>;
  onRegionClick: (regionId: number, regionName: string) => void;
  initialRegionId?: number;
}> = React.memo(({ polygonsData, onRegionClick, initialRegionId }) => {
  const map = useMap();
  const polygonLayersRef = useRef<{ [key: string]: L.Polygon }>({});
  const activePolygonRef = useRef<L.Polygon | null>(null);

  const resetPolygonStyles = useCallback(() => {
    Object.values(polygonLayersRef.current).forEach(layer => {
      layer.setStyle({
        fillOpacity: 0.1,
        weight: 1,
        color: 'rgba(77, 142, 33, 1)',
        fillColor: 'rgba(77, 142, 33, 1)'
      });
    });
    activePolygonRef.current = null;
  }, []);

  const setActivePolygonStyle = useCallback((polygonLayer: L.Polygon) => {
    if (activePolygonRef.current !== polygonLayer) {
      resetPolygonStyles();
      polygonLayer.setStyle({
        fillOpacity: 0.3,
        weight: 2,
        color: 'green',
        fillColor: 'green'
      });
      activePolygonRef.current = polygonLayer;
    }
  }, [resetPolygonStyles]);

  useEffect(() => {
    if (!map || !polygonsData?.res) return;

    const polygonsArray = Array.isArray(polygonsData.res) ? polygonsData.res : [polygonsData.res];

    Object.values(polygonLayersRef.current).forEach(layer => layer.remove());
    polygonLayersRef.current = {};

    polygonsArray.forEach((polygon) => {
      const latLngs = parsePolygonData(polygon);
      if (!latLngs) return;

      const polygonLayer = L.polygon(latLngs, {
        color: 'rgba(77, 142, 33, 1)',
        fillColor: 'rgba(77, 142, 33, 1)',
        fillOpacity: 0.1,
        weight: 1,
        className: styles.clickablePolygon
      }).addTo(map);

      polygonLayer.on('click', () => {
        if (polygon.id != null) {
          setActivePolygonStyle(polygonLayer);
          onRegionClick(polygon.id, polygon.nameRu);
        }
      });

      if (polygon.id != null) {
        polygonLayersRef.current[polygon.id.toString()] = polygonLayer;
        
        // Установка начального активного полигона
        if (initialRegionId && polygon.id === initialRegionId) {
          setActivePolygonStyle(polygonLayer);
        }
      }
    });

    const polygonLayers = Object.values(polygonLayersRef.current);
    if (polygonLayers.length > 0) {
      const group = L.featureGroup(polygonLayers);
      map.fitBounds(group.getBounds());
    }
  }, [map, polygonsData, onRegionClick, setActivePolygonStyle, initialRegionId]);

  return null;
});

export const LeasingMap: React.FC<LeasingMapProps> = ({
  latitude,
  longitude,
  polygonsData,
  transportTypes,
  isLoading,
  onRegionClick,
  selectedRegionName,
  selectedYear,
  onYearChange,
  initialRegionId
}) => {
  if (isLoading) {
    return (
      <div className={styles.loadingContainer}>
        <div className={styles.loader}></div>
        <div className={styles.loaderText}>Загрузка данных...</div>
      </div>
    );
  }

  return (
    <div className={styles.container}>
      <div className={styles.mapWrapper}>
        <div className={styles.yearSelector}>
          <LeasingSelectYear value={selectedYear} onChange={onYearChange} />
        </div>
        <MapContainer
          center={[latitude, longitude]}
          zoom={6}
          minZoom={6}
          maxZoom={6}
          dragging={false}
          scrollWheelZoom={false}
          doubleClickZoom={false}
          touchZoom={false}
          style={{ height: '100%', width: '100%' }}
          attributionControl={false}
          zoomControl={false}
        >
       <TileLayer
           attribution='&copy; <a href="https://www.openstreetmap.org/copyright">OpenStreetMap</a> contributors'
           url="https://{s}.tile.openstreetmap.org/{z}/{x}/{y}.png"
         />
          {polygonsData && <MapComponent polygonsData={polygonsData} onRegionClick={onRegionClick} initialRegionId={initialRegionId} />}
        </MapContainer>
      </div>

      {transportTypes && selectedRegionName && (
        <div className={styles.tableWrapper}>
          <div className={styles.tableHeader}>
            <h2 className={styles.title}>
              {`${selectedRegionName} ${selectedYear ? ` за ${selectedYear} год` : ''}`}
            </h2>
            <div className={styles.tableHeaderRow}>
              <div>Техника</div>
              <div>Кол-во</div>
            </div>
          </div>
          <div className={styles.tableContent}>
            {transportTypes.res.length > 0 ? (
              transportTypes.res.map((item, index) => (
                <div key={index} className={styles.tableRow}>
                  <div className={styles.techColumn}>
                    <span>{item.transportTypeNameRu}</span>
                  </div>
                  <div className={styles.countColumn}>{item.totalCount}</div>
                </div>
              ))
            ) : (
              <div className={styles.noData}>Нет данных</div>
            )}
          </div>
        </div>
      )}
    </div>
  );
};
